import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { TOURS } from "../data/content";
import { FadeUp, SectionHead, Img } from "./Reveal";
import { useEnquiry } from "./EnquiryContext";

export const FeaturedTours = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section id="tours" data-testid="tours-section" className="relative py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-16">
          <SectionHead
            num="03"
            eyebrow="Featured Tours"
            title="Journeys We've Perfected"
            copy="Signature itineraries through the forests and monuments of the heartland. Every tour is refined on request — nothing here is fixed in stone."
          />
        </div>

        <div className="flex flex-col gap-16 lg:gap-24">
          {TOURS.map((t, i) => (
            <FadeUp key={t.slug} className="group">
              <div
                data-testid={`tour-card-${t.slug}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center ${
                  i % 2 === 1 ? "lg:[direction:rtl]" : ""
                }`}
              >
                <div className="relative overflow-hidden h-72 sm:h-96 lg:h-[460px] [direction:ltr]">
                  <Img
                    src={t.img}
                    alt={t.name}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-108 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/30 transition-colors duration-700" />
                  <div className="absolute top-5 left-5 glass-panel px-4 py-2 flex items-center gap-2">
                    <Clock size={12} className="text-gold" />
                    <span className="font-mono text-[11px] tracking-[0.18em] text-cream">
                      {t.duration}
                    </span>
                  </div>
                </div>

                <div className="[direction:ltr]">
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                    {t.destination}
                  </span>
                  <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream mt-3 tracking-tight">
                    {t.name}
                  </h3>
                  <p className="font-serif italic text-lg text-sage mt-2">{t.tagline}</p>
                  <p className="text-sm text-cream/60 leading-relaxed mt-5 max-w-lg line-clamp-3">
                    {t.overview}
                  </p>

                  <ul className="mt-6 space-y-2">
                    {t.highlights.slice(0, 3).map((h) => (
                      <li key={h} className="flex items-start gap-3 text-sm text-cream/70">
                        <span className="w-1.5 h-1.5 rotate-45 bg-gold mt-1.5 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                    <span className="font-mono text-xs tracking-[0.2em] text-terracotta uppercase">
                      {t.price}
                    </span>
                    <div className="flex items-center gap-4">
                      <Link
                        to={`/tours/${t.slug}`}
                        data-testid={`tour-view-details-${t.slug}`}
                        className="group/link flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cream/80 hover:text-gold transition-colors"
                      >
                        View Details
                        <ArrowRight size={13} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                      </Link>
                      <button
                        data-testid={`tour-enquire-${t.slug}`}
                        onClick={() => openEnquiry({ destination: t.name })}
                        className="bg-gold text-forest-950 font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-3 hover:bg-cream transition-colors duration-300"
                      >
                        Book / Enquire
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
